// apps/provider/src/services/api/documents.js
// Patient document management methods
//
// Methods: getDocumentCategories, createDocumentCategory, updateDocumentCategory,
//          deleteDocumentCategory, uploadPatientDocument, updatePatientDocument,
//          getPatientDocuments, deletePatientDocument
//
// Response types: @prism-ehr/shared → DocumentCategory, PatientDocument

/**
 * @returns {Object} Documents domain methods (bound to `this` of the composed apiService)
 */
export function createDocumentsMethods() {
  return {
    /**
     * Get document categories for an organization.
     *
     * Xano: GET /document_category
     * Xano file: 819_document_category_GET.xs
     *
     * @param {string} organizationId
     * @returns {Promise<import('@prism-ehr/shared').DocumentCategory[]>}
     */
    getDocumentCategories(organizationId) {
      return this.request(`/document_category?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new document category.
     *
     * Xano: POST /document_category
     * Xano file: 820_document_category_POST.xs
     *
     * @param {Object} categoryData
     * @returns {Promise<import('@prism-ehr/shared').DocumentCategory>}
     */
    createDocumentCategory(categoryData) {
      return this.request('/document_category', {
        method: 'POST',
        body: JSON.stringify(categoryData)
      });
    },

    /**
     * Update a document category.
     *
     * Xano: PATCH /document_category/{document_category_id}
     * Xano file: 821_document_category_document_category_id_PATCH.xs
     *
     * @param {string|number} categoryId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').DocumentCategory>}
     */
    updateDocumentCategory(categoryId, updates) {
      return this.request(`/document_category/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a document category.
     *
     * Xano: DELETE /document_category/{document_category_id}
     * Xano file: 817_document_category_document_category_id_DELETE.xs
     *
     * @param {string|number} categoryId
     * @returns {Promise<null>}
     */
    deleteDocumentCategory(categoryId) {
      return this.request(`/document_category/${categoryId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Upload a patient document (converts file to base64).
     *
     * Xano: POST /patient_document
     * Xano file: 815_patient_document_POST.xs
     *
     * @param {Object} documentData
     * @param {string} documentData.patient_id
     * @param {string} documentData.category_id
     * @param {string} documentData.name
     * @param {string} [documentData.description]
     * @param {string} [documentData.document_date]
     * @param {string} [documentData.provider_name]
     * @param {string} [documentData.linked_order_id]
     * @param {'provider_order'|'lab_order'} [documentData.linked_order_type]
     * @param {number} [documentData.case_id] - Case this document belongs to
     * @param {File} file
     * @returns {Promise<import('@prism-ehr/shared').PatientDocument>}
     */
    async uploadPatientDocument(documentData, file) {
      const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      };

      const base64Data = await fileToBase64(file);

      // Ensure document_name includes the file extension so Xano stores the file correctly
      let docName = documentData.name || file.name;
      const fileExt = file.name.includes('.') ? file.name.substring(file.name.lastIndexOf('.')) : '';
      if (fileExt && !docName.toLowerCase().endsWith(fileExt.toLowerCase())) {
        docName = `${docName}${fileExt}`;
      }

      const body = {
        patient_id: documentData.patient_id,
        category_id: documentData.category_id,
        document_name: docName,
        description: documentData.description || '',
        document_date: documentData.document_date || '',
        provider_name: documentData.provider_name || '',
        file_data: base64Data,
        file_name: file.name,
        file_type: file.type,
        file_size: file.size
      };

      if (documentData.linked_order_id) body.linked_order_id = documentData.linked_order_id;
      if (documentData.linked_order_type) body.linked_order_type = documentData.linked_order_type;
      if (documentData.linked_record_id) body.linked_record_id = documentData.linked_record_id;
      if (documentData.case_id) body.case_id = documentData.case_id;

      return this.request('/patient_document', {
        method: 'POST',
        timeoutMs: 120000,
        body: JSON.stringify(body)
      });
    },

    /**
     * Update a patient document.
     *
     * Xano: PATCH /patient_document/{id}
     * Xano file: 816_patient_document_patient_document_id_PATCH.xs
     *
     * @param {string|number} documentId
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').PatientDocument>}
     */
    updatePatientDocument(documentId, updates) {
      return this.request(`/patient_document/${documentId}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Get patient documents, optionally filtered by category.
     *
     * Xano: GET /patient_document
     * Xano file: 814_patient_document_GET.xs
     *
     * @param {string} patientId
     * @param {string|null} [categoryId]
     * @returns {Promise<import('@prism-ehr/shared').PatientDocument[]>}
     */
    getPatientDocuments(patientId, categoryId = null) {
      let endpoint = `/patient_document?patient_id=${patientId}`;
      if (categoryId) endpoint += `&category_id=${categoryId}`;

      return this.request(endpoint, {
        method: 'GET'
      });
    },

    /**
     * Get patient documents with pagination, server-side category filtering, and sort.
     *
     * Xano: GET /patient-documents
     * Xano file: patient-documents_GET.xs
     *
     * @param {Object} filters
     * @param {string} filters.patient_id
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @param {string} [filters.category_id]
     * @param {number} [filters.case_id]
     * @param {'asc'|'desc'} [filters.sort_direction]
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<import('@prism-ehr/shared').PatientDocument>>}
     */
    getPatientDocumentsPaginated(filters) {
      const params = new URLSearchParams();
      params.append('patient_id', filters.patient_id);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.case_id) params.append('case_id', filters.case_id);
      if (filters.sort_direction) params.append('sort_direction', filters.sort_direction);

      return this.request(`/patient-documents?${params.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Delete a patient document.
     *
     * Xano: DELETE /patient_document/{id}
     * Xano file: 812_patient_document_patient_document_id_DELETE.xs
     *
     * @param {string} documentId
     * @returns {Promise<null>}
     */
    deletePatientDocument(documentId) {
      return this.request(`/patient_document/${documentId}`, {
        method: 'DELETE'
      });
    },

    /**
     * Get a patient document linked to a specific record (clinical note or plan of care).
     *
     * Xano: GET /patient_document?linked_record_id={id}
     *
     * @param {string} linkedRecordId
     * @returns {Promise<import('@prism-ehr/shared').PatientDocument|null>}
     */
    async getLinkedDocument(linkedRecordId) {
      const results = await this.request(`/patient_document?linked_record_id=${linkedRecordId}`, {
        method: 'GET'
      });
      const items = results?.items || results || [];
      return items[0] || null;
    },

    /**
     * Get message attachments for a patient (files sent via messaging).
     *
     * Xano: GET /patient-attachments/messages
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').PatientMessageAttachment[]>}
     */
    getPatientMessageAttachments(patientId) {
      return this.request(`/patient-attachments/messages?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Get form response attachments for a patient (files uploaded via forms).
     *
     * Xano: GET /patient-attachments/forms
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').PatientFormAttachment[]>}
     */
    getPatientFormAttachments(patientId) {
      return this.request(`/patient-attachments/forms?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Get inbound faxes linked to a patient that carry a document.
     *
     * Xano: GET /patient-attachments/faxes
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').PatientFaxAttachment[]>}
     */
    getPatientFaxAttachments(patientId) {
      return this.request(`/patient-attachments/faxes?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    /**
     * Promote a message, form, or fax attachment to a patient chart document.
     *
     * Xano: POST /patient-document/promote
     *
     * @param {import('@prism-ehr/shared').PromoteAttachmentRequest} promoteData
     * @returns {Promise<import('@prism-ehr/shared').PatientDocument>}
     */
    promoteAttachmentToDocument(promoteData) {
      return this.request('/patient-document/promote', {
        method: 'POST',
        body: JSON.stringify(promoteData)
      });
    },
  };
}
